import { Eye, Globe, MapPin, Crown, User as UserIcon, Sparkles, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useGeoOverride } from '@/hooks/useGeoOverride';
import { useViewAs, ViewAsRole } from '@/hooks/useViewAs';

const ROLE_OPTIONS: { value: ViewAsRole; label: string; icon: React.ReactNode }[] = [
  { value: 'auto', label: 'Admin (real)', icon: <Sparkles className="w-3.5 h-3.5" /> },
  { value: 'member', label: 'Free member', icon: <UserIcon className="w-3.5 h-3.5" /> },
  { value: 'premium', label: 'Club Premium', icon: <Sparkles className="w-3.5 h-3.5" /> },
  { value: 'founding', label: 'Founding Member', icon: <Crown className="w-3.5 h-3.5" /> },
];

export function AdminToolbar() {
  const { isRealAdmin } = useAuth();
  const { geoMode, toggleGeo } = useGeoOverride();
  const { role, setRole, isOverridden } = useViewAs();
  const [open, setOpen] = useState(false);

  if (!isRealAdmin) return null;

  const current = ROLE_OPTIONS.find((o) => o.value === role) ?? ROLE_OPTIONS[0];
  const geoLabel = geoMode === 'international' ? 'International' : geoMode === 'india' ? 'India' : 'Auto';

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2">
      {open && (
        <div className="rounded-xl border border-border/40 bg-card/95 shadow-2xl backdrop-blur-md p-2 w-56">
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            View as
          </div>
          {ROLE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setRole(opt.value)}
              className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs transition-colors ${
                role === opt.value
                  ? 'bg-accent/15 text-accent'
                  : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              {opt.icon}
              <span>{opt.label}</span>
              {role === opt.value && <span className="ml-auto text-[10px]">●</span>}
            </button>
          ))}
          <div className="border-t border-border/30 my-2" />
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            Geo
          </div>
          <button
            onClick={toggleGeo}
            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            {geoMode === 'international' ? <Globe className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
            <span>Toggle geo · {geoLabel}</span>
          </button>
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium shadow-lg backdrop-blur-md transition-all ${
          isOverridden
            ? 'border-accent/60 bg-accent/15 text-accent'
            : 'border-border/40 bg-card/90 text-muted-foreground hover:text-foreground'
        }`}
      >
        <Eye className="h-3.5 w-3.5" />
        <span>Viewing as: {current.label}</span>
        <ChevronUp className={`h-3 w-3 transition-transform ${open ? '' : 'rotate-180'}`} />
      </button>
    </div>
  );
}